import React, { useState, useEffect, useCallback } from "react";
import { Button, CircularProgress, Backdrop, Typography, Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Form } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";
import { GWP_BASES } from "./constants/wasteTaxonomy";

/**
 * EcoPrism staff: the waste emission-factor library. Lists imported editions, shows one edition's rows,
 * activates or marks an edition verified, and imports a new edition from the DEFRA-style workbook
 * (parsed in the browser into header → value rows, like every other upload in the platform).
 * The API re-checks the EcoPrism admin table on every call.
 */
function WasteFactorLibrary() {
  const username = localStorage.getItem("userName");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [editions, setEditions] = useState([]);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const [form, setForm] = useState({ editionId: "", source: "UK DEFRA / DESNZ, Government Conversion Factors for Company Reporting", publishedOn: "", gwpBasis: "AR5", notes: "", activate: false, replaceExisting: false });
  const [importRows, setImportRows] = useState([]);
  const [importFileName, setImportFileName] = useState("");
  const [importResult, setImportResult] = useState(null);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const renderHeader = () =>
    localStorage.getItem("loginType") === "AAD" ? (
      <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    ) : (
      <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />
    );

  const call = useCallback(
    async (route, { method = "GET", headers = {}, body } = {}) => {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}${route}`, {
        method,
        headers: { "Content-type": "application/json", username, ...headers },
        body: body ? JSON.stringify(body) : undefined,
      });
      const json = await response.json().catch(() => ({}));
      if (response.status !== 200 || (json && json.success === false)) {
        const message = json && json.data && typeof json.data === "object" && Array.isArray(json.data.errors) ? json.data : (json && json.data) || `${route} failed (${response.status})`;
        throw Object.assign(new Error(typeof message === "string" ? message : "Import rejected"), { payload: message });
      }
      return json && json.data !== undefined ? json.data : json;
    },
    [username]
  );

  const loadEditions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await call("waste-factor-editions");
      setEditions(Array.isArray(list) ? list : []);
      const active = (Array.isArray(list) ? list : []).find((e) => e.isActive);
      if (active && !selectedEdition) setSelectedEdition(active.editionId);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [call, selectedEdition]);

  const loadRows = useCallback(
    async (editionId) => {
      if (!editionId) return;
      setLoading(true);
      try {
        const data = await call("waste-factor-edition", { headers: { edition: editionId } });
        setRows(Array.isArray(data && data.rows) ? data.rows : []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [call]
  );

  useEffect(() => {
    loadEditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    loadRows(selectedEdition);
  }, [selectedEdition, loadRows]);

  const activate = async (editionId) => {
    const ok = await Swal.fire({ icon: "question", title: `Activate ${editionId}?`, text: "New uploads will use this edition. Rows already calculated keep the edition they used.", showCancelButton: true, confirmButtonText: "Activate", confirmButtonColor: "#407775" });
    if (!ok.isConfirmed) return;
    try {
      await call("waste-factor-activate", { method: "POST", body: { editionId } });
      await loadEditions();
      Swal.fire({ icon: "success", text: `${editionId} is now the active edition.` });
    } catch (e) {
      Swal.fire({ icon: "error", text: e.message });
    }
  };

  const setVerified = async (editionId, verified) => {
    try {
      await call("waste-factor-verify", { method: "POST", body: { editionId, verified } });
      await loadEditions();
      if (editionId === selectedEdition) await loadRows(editionId);
    } catch (e) {
      Swal.fire({ icon: "error", text: e.message });
    }
  };

  const onFile = (e) => {
    const file = e.target.files && e.target.files[0];
    setImportRows([]);
    setImportResult(null);
    if (!file) return;
    setImportFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const normalised = json.map((r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [String(k).trim(), v == null ? "" : String(v)])));
        setImportRows(normalised);
        if (!form.editionId) {
          const guess = file.name.replace(/\.[^.]+$/, "").replace(/[^A-Za-z0-9._-]+/g, "-").slice(0, 40);
          setForm((f) => ({ ...f, editionId: guess }));
        }
      } catch (err) {
        Swal.fire({ icon: "error", text: `Could not read the workbook: ${err.message}` });
      }
    };
    reader.readAsBinaryString(file);
  };

  const runImport = async () => {
    if (!form.editionId) return Swal.fire({ icon: "warning", text: "Enter an edition id (e.g. DEFRA-2026)." });
    if (importRows.length === 0) return Swal.fire({ icon: "warning", text: "Choose a workbook first." });
    setLoading(true);
    setImportResult(null);
    try {
      const result = await call("waste-factor-import", { method: "POST", body: { ...form, rows: importRows } });
      setImportResult(result);
      await loadEditions();
      setSelectedEdition(form.editionId);
      Swal.fire({ icon: "success", text: `Imported ${result.rowsImported} rows (${result.rowsWithFactor} with factors, ${result.proxies} proxies)${result.activated ? " and activated" : ""}.` });
    } catch (e) {
      setImportResult(e.payload && typeof e.payload === "object" ? e.payload : { errors: [e.message] });
    } finally {
      setLoading(false);
    }
  };

  const factorBody = (r) => (r.factorKgPerTonne == null ? <span style={{ color: "#B26A00" }}>no factor</span> : Number(r.factorKgPerTonne).toLocaleString(undefined, { maximumFractionDigits: 4 }));
  const boolBody = (v) => (v ? "Yes" : "—");
  const dateBody = (v) => (v ? new Date(v).toLocaleDateString() : "—");

  return (
    <div className={`wrapper ${isSidebarOpen ? "" : "toggled"}`}>
      {loading && (
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container" style={{ paddingLeft: "30px", paddingRight: "20px" }}>
          <div className="steps__containt-hader">
            <h3 className="title mb-0">Waste factor library</h3>
            <div role="presentation" className="mt-2">
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                <Link underline="hover" color="inherit" href="/organizations" className="link">
                  Settings
                </Link>
                <Typography color="text.primary">Waste factor library</Typography>
              </Breadcrumbs>
            </div>
          </div>
          {error && <div className="alert alert-warning mt-3">{error}</div>}

          {/* Editions */}
          <div className="card border-0 shadow p-3 mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span style={{ fontSize: "16px", color: "#494949" }}>Editions</span>
              <Button variant="text" onClick={loadEditions} sx={{ textTransform: "none", color: "#407775" }}>
                Refresh
              </Button>
            </div>
            <DataTable value={editions} size="small" stripedRows selectionMode="single" selection={editions.find((e) => e.editionId === selectedEdition) || null} onSelectionChange={(e) => e.value && setSelectedEdition(e.value.editionId)} dataKey="editionId" emptyMessage="No editions yet. Import one below.">
              <Column field="editionId" header="Edition" body={(r) => (<span>{r.editionId} {r.isActive && <span className="badge" style={{ background: "#407775" }}>active</span>}</span>)} />
              <Column field="source" header="Source" />
              <Column field="publishedOn" header="Published" />
              <Column header="Rows" body={(r) => `${r.rowCount} (${r.rowsWithFactor} with factor, ${r.proxyRows} proxy)`} />
              <Column header="Imported" body={(r) => (r.importedAt ? `${dateBody(r.importedAt)} · ${r.importedBy || ""}` : r.notes ? "outside the import endpoint" : "—")} />
              <Column header="Verified vs official" body={(r) => (r.verifiedAgainstOfficial ? `Yes · ${r.verifiedBy || ""}` : <span style={{ color: "#B26A00" }}>No</span>)} />
              <Column
                header="Actions"
                body={(r) => (
                  <div className="d-flex gap-2">
                    {!r.isActive && (
                      <Button size="small" variant="outlined" onClick={() => activate(r.editionId)} sx={{ textTransform: "none", borderColor: "#407775", color: "#407775" }}>
                        Activate
                      </Button>
                    )}
                    <Button size="small" variant="text" onClick={() => setVerified(r.editionId, !r.verifiedAgainstOfficial)} sx={{ textTransform: "none", color: "#407775" }}>
                      {r.verifiedAgainstOfficial ? "Mark unverified" : "Mark verified"}
                    </Button>
                  </div>
                )}
              />
            </DataTable>
          </div>

          {/* Rows of the selected edition */}
          <div className="card border-0 shadow p-3 mt-3">
            <span style={{ fontSize: "16px", color: "#494949" }}>Factors in {selectedEdition || "…"}</span>
            <DataTable value={rows} size="small" stripedRows paginator rows={15} sortField="wasteType" sortOrder={1} emptyMessage="Select an edition.">
              <Column field="wasteCategory" header="Category" sortable />
              <Column field="wasteType" header="Waste type" sortable />
              <Column field="disposalMethod" header="Disposal method" sortable body={(r) => (r.disposalMethod === "*" ? "Any disposal method" : r.disposalMethod)} />
              <Column header="kg CO2e / t" body={factorBody} sortable sortField="factorKgPerTonne" />
              <Column field="factorScope" header="Scope" body={(r) => (r.factorScope || "").split(" (")[0]} />
              <Column field="gwpBasis" header="GWP" />
              <Column header="Proxy" body={(r) => boolBody(r.isProxy)} />
              <Column field="referenceActivity" header="Reference activity" />
              <Column field="notes" header="Notes" />
            </DataTable>
          </div>

          {/* Import */}
          <div className="card border-0 shadow p-3 mt-3 mb-5">
            <span style={{ fontSize: "16px", color: "#494949" }}>Import an edition</span>
            <p style={{ fontSize: "13px", color: "#707070" }}>
              Workbook columns: Waste Category, Waste Type, Disposal Method ("Any disposal method" allowed), Matched Reference Activity, Emission Factor (kg CO2e / tonne, blank = no published factor), Factor Scope, Source Database, Year / Edition, GWP Basis, Notes. Nothing is written if any row fails.
            </p>
            <div className="row g-3">
              <div className="col-12 col-md-3">
                <Form.Label>Edition id *</Form.Label>
                <Form.Control value={form.editionId} placeholder="DEFRA-2026" onChange={(e) => setForm({ ...form, editionId: e.target.value })} />
              </div>
              <div className="col-12 col-md-5">
                <Form.Label>Source</Form.Label>
                <Form.Control value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>Published on</Form.Label>
                <Form.Control type="date" value={form.publishedOn} onChange={(e) => setForm({ ...form, publishedOn: e.target.value })} />
              </div>
              <div className="col-6 col-md-2">
                <Form.Label>GWP basis</Form.Label>
                <Form.Select value={form.gwpBasis} onChange={(e) => setForm({ ...form, gwpBasis: e.target.value })}>
                  {GWP_BASES.map((g) => (
                    <option key={g}>{g}</option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-12 col-md-8">
                <Form.Label>Notes</Form.Label>
                <Form.Control value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="col-12 col-md-4 d-flex align-items-end gap-4">
                <Form.Check label="Activate after import" checked={form.activate} onChange={(e) => setForm({ ...form, activate: e.target.checked })} />
                <Form.Check label="Replace if it exists" checked={form.replaceExisting} onChange={(e) => setForm({ ...form, replaceExisting: e.target.checked })} />
              </div>
              <div className="col-12 col-md-8">
                <Form.Label>Workbook (.xlsx)</Form.Label>
                <Form.Control type="file" accept=".xlsx,.xls,.csv" onChange={onFile} />
                {importFileName && (
                  <small style={{ color: "#707070" }}>
                    {importFileName}: {importRows.length} data rows parsed.
                  </small>
                )}
              </div>
              <div className="col-12 col-md-4 d-flex align-items-end">
                <Button variant="contained" onClick={runImport} disabled={loading || importRows.length === 0} sx={{ textTransform: "none", background: "#407775" }}>
                  Import edition
                </Button>
              </div>
            </div>
            {importResult && (
              <div className="mt-3" style={{ fontSize: "13px" }}>
                {Array.isArray(importResult.errors) && importResult.errors.length > 0 ? (
                  <div className="alert alert-danger mb-0">
                    <b>Import rejected; nothing was written.</b>
                    <ul className="mb-0">
                      {importResult.errors.map((er, i) => (
                        <li key={i}>{er}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="alert alert-success mb-0">
                    Imported {importResult.rowsImported} rows: {importResult.rowsWithFactor} with a factor, {importResult.rowsWithoutFactor} documented gaps, {importResult.proxies} proxies.
                    {Array.isArray(importResult.warnings) && importResult.warnings.length > 0 && (
                      <ul className="mb-0 mt-2">
                        {importResult.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WasteFactorLibrary;
